/**
 * Four promises, one line each — the things a buyer asks after "what does it
 * do?", answered without a paragraph.
 *
 * Each is something the product does today:
 *   - your own accounts: the workspace connects its own SES, WhatsApp number
 *     and Meta ad account; Wend resells none of them, so the channel's price is
 *     the reader's own;
 *   - nothing runs on its own: Sense suggests and drafts, a person launches
 *     every flow and starts every ad;
 *   - every column becomes a rule: slice 67's field catalogue;
 *   - quiet hours and caps: the flow engine and the campaign path both apply
 *     them to every send.
 */
import type { ReactNode } from 'react';

const icon = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.7, strokeLinecap: 'round', strokeLinejoin: 'round' } as const;

const PROOF: readonly { title: string; line: string; glyph: ReactNode }[] = [
  {
    title: 'Your own accounts',
    line: 'The channel price stays yours.',
    glyph: (
      <>
        <circle cx="8" cy="12" r="4" {...icon} />
        <path d="M12 12h9M18 12v3M21 12v2" {...icon} />
      </>
    ),
  },
  {
    title: 'Nothing runs on its own',
    line: 'Sense drafts, you launch.',
    glyph: (
      <>
        <rect x="4" y="4" width="16" height="16" rx="4" {...icon} />
        <path d="M8.5 12.5l2.5 2.5 4.5-5" {...icon} />
      </>
    ),
  },
  {
    title: 'Every column becomes a rule',
    line: 'Birthday, tier, city: whatever you upload.',
    glyph: (
      <>
        <rect x="3.5" y="5" width="17" height="14" rx="2" {...icon} />
        <path d="M3.5 10h17M9.5 5v14" {...icon} />
      </>
    ),
  },
  {
    title: 'Quiet hours and caps',
    line: 'On every send, campaign or flow.',
    glyph: <path d="M19 14.5A7.5 7.5 0 0 1 9.5 5a7.5 7.5 0 1 0 9.5 9.5z" {...icon} />,
  },
];

export function Proof() {
  return (
    <section id="proof" aria-label="What you can count on" className="border-y border-line bg-surface-1/50 px-5 py-14 sm:px-8 sm:py-16">
      <ul className="reveal mx-auto grid max-w-6xl gap-8 sm:grid-cols-2 lg:grid-cols-4">
        {PROOF.map((item) => (
          <li key={item.title} className="flex items-start gap-3.5">
            <span className="grid size-11 flex-none place-items-center rounded-[12px] border border-brand-b/35 bg-brand-a/15 text-brand-ink">
              <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
                {item.glyph}
              </svg>
            </span>
            <span>
              <span className="block text-[1rem] font-semibold text-text">{item.title}</span>
              <span className="mt-0.5 block text-[0.9rem] text-muted">{item.line}</span>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
